import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { selectionSet, type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { setProductCancleStatus } from '@adornis/buchungsheld-db/api/product.js';
import { AdminInstitutionViewSetting } from '@adornis/buchungsheld-db/entities/AdminInstitutionViewSetting.js';
import { BHFilter } from '@adornis/buchungsheld-db/entities/bh-filter.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { dataviewEmptyState, productPrice } from '@adornis/buchungsheld-db/global-helpers.js';
import { getPluginForScope } from '@adornis/buchungsheld-db/plugins/plugin.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { DesignSystem, type ThemeDefinition } from '@adornis/chemistry/elements/theming/design.js';
import { buildFullOptions, type DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { askForTextFile, downloadLink } from '@adornis/file-utils/client/helper-methods.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { watch } from '@lit-labs/preact-signals';
import { html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { firstValueFrom, timer } from 'rxjs';
import { useContextEntity } from '../../../../util.js';
import './bh-page--admin-einrichtung-workshops-modify.js';
import './bh-page-admin-einrichtung-workshop-detail.js';
import './bh-page-admin-einrichtung-workshops-edit.js';
import './bh-page-admin-einrichtung-workshops-notes.js';

const breadcrumbConfig = (params, label: string) => [
  { content: 'Workshops', href: `/admin/institution-details/${params.institutionID}/workshops` },
  { content: label, active: true },
];

export const adminEinrichtungWorkshopsRouter = makeRouter(
  {
    overview: {
      path: '/',
      metaData: {
        breadcrumbs: ({ params }) => [{ content: 'Workshops', active: true }],
        semesterSelect: true,
      },
      render: params => {
        return stateful(() => {
          const [viewSettings] = useContextEntity(AdminInstitutionViewSetting)({ selectedTimeframeID: 1 });
          const design = useObservable(() => DesignSystem.currentTheme, []);

          const filter = !params.institutionID
            ? null
            : new BHFilter({
                institutionID: params.institutionID,
                timeframeID: viewSettings?.selectedTimeframeID,
                aspects: [],
                searchFields: ['name', 'description'],
                sortingDirections: ['asc'],
                sortingFields: ['sortingWeight'],
              });

          return renderWorkshopsListing({ institutionID: params.institutionID, filter, design });
        })();
      },
    },
    add: {
      path: '/add',
      metaData: {
        breadcrumbs: ({ params }) => breadcrumbConfig(params, 'Workshop anlegen'),
      },
      render: stateful(params => {
        return html`
          <bh-page-admin-einrichtung-workshops-modify
            institution-id=${params.institutionID}
            mode="add"
            @saved=${() => goTo(`/admin/institution-details/${params.institutionID}/workshops`)}
          ></bh-page-admin-einrichtung-workshops-modify>
        `;
      }),
    },
    notes: {
      metaData: {
        breadcrumbs: ({ params }) => breadcrumbConfig(params, 'Notizen'),
      },
      path: '/notes/:workshopID',
      render: params =>
        html`
          <bh-page-admin-einrichtung-workshops-notes
            workshop-id=${params.workshopID}
            institution-id=${params.institutionID}
          ></bh-page-admin-einrichtung-workshops-notes>
        `,
    },
    detail: {
      metaData: {
        breadcrumbs: ({ params }) => breadcrumbConfig(params, 'Detail'),
      },
      path: '/detail/:workshopID',
      render: stateful(
        params =>
          html`
            <bh-page-admin-einrichtung-workshop-detail
              workshop-id=${params.workshopID}
              institution-id=${params.institutionID}
            ></bh-page-admin-einrichtung-workshop-detail>
          `,
      ),
    },
  },
  {
    fallback: () => html`<x-empty-placeholder>404 - Seite nicht gefunden</x-empty-placeholder>`,
  },
);
const getLsKey = (institutionID: string) => `extra_columns:${institutionID}:workshops:v1`;

function getCachedExtraColumns(institutionID: string) {
  const lsKey = getLsKey(institutionID);
  const extraColumns = JSON.parse(localStorage.getItem(lsKey) ?? '[]');
  return extraColumns;
}

function setCachedExtraColumns(institutionID: string, columns: string[]) {
  const lsKey = getLsKey(institutionID);
  localStorage.setItem(lsKey, JSON.stringify(columns));
}

/**
 * @element bh-page-admin-einrichtung-workshops
 */
@customElement('bh-page-admin-einrichtung-workshops')
export class BHPageAdminEinrichtungWorkshops extends BuchungsheldLitElement {
  override render() {
    return html` ${watch(adminEinrichtungWorkshopsRouter.outlet)} `;
  }
}

const renderWorkshopsListing = stateful(
  ({
    institutionID,
    filter,
    design,
  }: {
    institutionID: Maybe<string>;
    filter: Maybe<BHFilter>;
    design: ThemeDefinition<any> | undefined;
  }) => {
    const [viewSettings] = useContextEntity(AdminInstitutionViewSetting)({
      institutionDetailsWorkshopsFilterV1: selectionSet(() => BHFilter, 5),
    });

    if (!filter || !institutionID) return html` <tr-loading-state></tr-loading-state> `;
    const savedAspects = viewSettings?.institutionDetailsWorkshopsFilterV1?.aspects;
    const savedSortFields = viewSettings?.institutionDetailsWorkshopsFilterV1?.sortingFields;
    const savedSortDirections = viewSettings?.institutionDetailsWorkshopsFilterV1?.sortingDirections;
    if (savedAspects && savedAspects.length > 0) filter.aspects = savedAspects as any;
    if (savedSortFields && savedSortFields.length > 0) filter.sortingFields = savedSortFields as any;
    if (savedSortDirections && savedSortDirections.length > 0) filter.sortingDirections = savedSortDirections as any;

    const extraColumns = getCachedExtraColumns(institutionID).map(fieldName => ({ fieldName }));
    const { dataViewOptions, filterOptions } = buildFullOptions({
      class: Workshop,
      operation: 'subscribeWorkshopsPaginated',
      isSortable: true,
      isColumnSelectable: true,
      emptyState: () => dataviewEmptyState({}),
      view: new View({ filter }),
      onViewChanged: async view => {
        onViewUpdate(institutionID, dataViewOptions, view);
        // TODO: save filter in onSortUpdate Event or somehow ignore dataview update
        // await saveFilter(viewSettings, view.filter);
      },
      isEntryClickable: true,
      defaultFieldMapping: {
        string: (value: string) => {
          if (value === 'RUNNING') return 'laufend';
          if (value === 'COMPLETED') return 'abgeschlossen';
          if (value === 'PLANNED') return 'geplant';
          return value;
        },
      },
      customColumns: [
        { fieldName: 'category.name', columnName: 'Kategorie', columnWidth: '25ch' },
        { fieldName: 'name', isSortable: true, columnName: 'Name', columnWidth: '36ch' },
        {
          columnName: 'Preis',
          fieldName: 'price',
          isSortable: true,
          columnWidth: 'max-content',
          selectionSet: { hasPrice: 1, prices: { _id: 1, unitAmount: 1 } },
          renderItem: instance => (instance.hasPrice ? html` <tr-text>${productPrice(instance)}</tr-text> ` : '---'),
        },
        {
          columnName: '#TN',
          fieldName: 'participationCount',
          columnWidth: 'max-content',
          selectionSet: {
            capacity: 1,
            leftCapacity: 1,
            participationCount: 1,
            highPriorityParticipationCount: 1,
            institutionID: 1,
            _id: 1,
          },
          renderItem: doc => renderCapacity(doc),
        },
        {
          columnName: 'Wartend',
          customID: 'numWaiting',
          columnWidth: 'max-content',
          selectionSet: { waitingParticipantsCount: 1 },
          renderItem: doc => {
            return html` <x-text ${css({ textAlign: 'center' })}> ${doc.waitingParticipantsCount} </x-text> `;
          },
        },
        {
          columnName: 'Reihenfolge',
          fieldName: 'sortingWeight',
          columnWidth: 'max-content',
          renderItem: e => html` <x-text align="right"> ${e.sortingWeight} </x-text> `,
        },

        ...extraColumns,

        {
          columnName: '',
          customID: '_options',
          selectionSet: { isCanceled: 1 },
          renderItem: doc => renderDataViewEntryOptions(doc, design),
          columnWidth: '1fr',
        },
      ],
    } satisfies DataViewOptions<typeof Workshop>);

    function onViewUpdate(institutionID: string, dataViewOptions: DataViewOptions<any>, view: View<any>) {
      const preExistingFields = dataViewOptions.customColumns!.map(col => col.fieldName).filter(isDefined);
      const cachedExtraFields = getCachedExtraColumns(institutionID);
      const selectionFields = view.configuredColumns!.map(col => col.fieldName).filter(isDefined);
      const newFields = selectionFields.filter(it => !preExistingFields.includes(it));
      const fieldsToBeRemoved = preExistingFields.filter(it => !selectionFields.includes(it));
      const newCachedExtraFields = cachedExtraFields.filter(it => !fieldsToBeRemoved.includes(it));
      for (const field of newFields) newCachedExtraFields.push(field);

      console.log({ newCachedExtraFields });
      setCachedExtraColumns(institutionID, newCachedExtraFields);

      view.configuredColumns?.sort((col1, col2) => {
        if (col1.customID === '_select') return -1;
        if (col1.customID === '_options') return 1;
        if (col2.customID === '_select') return 1;
        if (col2.customID === '_options') return -1;
        return 0;
      });

      // this.requestUpdate();
    }

    async function saveFilter(
      viewSettings: Maybe<
        NarrowedEntity<AdminInstitutionViewSetting, { institutionDetailsWorkshopsFilterV1: typeof BHFilter.allFields }>
      >,
      filter: any,
    ) {
      if (!viewSettings) return;
      viewSettings.institutionDetailsWorkshopsFilterV1 = filter;
      await viewSettings.save();
    }

    return html`
      <x-flex space="xl">
        ${renderImportExportSection(institutionID, filter?.timeframeID)}

        <x-hr></x-hr>

        <x-flex space="md">
          <x-flex space-between space="md" wrap horizontal crossaxis-center>
            <x-h3> ${clientTranslator.translate('workshop', 'plural')} </x-h3>
            <tr-button-small
              icon="add"
              .action=${() => goTo(`/admin/institution-details/${institutionID}/workshops/add`)}
            >
              <x-text> ${clientTranslator.translate('workshop', 'add')} </x-text>
            </tr-button-small>
          </x-flex>
          <x-filter-editor
            .options=${filterOptions}
            @filter-apply=${e => saveFilter(viewSettings, e.detail)}
            @filter-reset=${e => saveFilter(viewSettings, null)}
          ></x-filter-editor>
          <x-data-view
            .options=${dataViewOptions}
            @entry-clicked=${(e: CustomEvent) => {
              console.log('clicked', e.detail, e.detail);
              const workshopID = e.detail.entity._id;
              goTo(`/admin/institution-details/${institutionID}/workshops/detail/${workshopID}?mode=participants`);
              e.stopPropagation();
            }}
          >
          </x-data-view>
        </x-flex>
      </x-flex>
    `;
  },
);

function renderImportExportSection(institutionID: Maybe<string>, timeframeID?: string) {
  return html`
    <x-flex space="md">
      <x-h3> Import / Export </x-h3>
      <x-flex horizontal space="md">
        <x-button mode="outline" icon="download" @click=${() => exportWorkshops(institutionID, timeframeID)}>
          ${clientTranslator.translate('imp_exp', 'exp_workshops')}
        </x-button>
        <x-button mode="outline" icon="upload" @click=${() => importWorkshops(institutionID)}>
          ${clientTranslator.translate('imp_exp', 'imp_workshops')}
        </x-button>
      </x-flex>
    </x-flex>
  `;
}

function renderCapacity(doc) {
  const segment = (count, capacity) => {
    const ratio = count / capacity;
    const backgroundColor = (() => {
      if (Number.isNaN(ratio)) return 'lightgray';
      if (ratio < 0.4) return '#bc1b1b';
      if (ratio < 0.7) return '#ffa422';
      return '#39db5c';
    })();

    return html`
      <x-flex horizontal space="xs" crossaxis-center ${css({ justifyContent: 'end' })}>
        <x-text ${css({ textAlign: 'right' })}> ${count} von ${doc.capacity} </x-text>
        <span
          ${css({
            display: 'inline-block',
            border: '1px solid lightgray',
            borderRadius: '50%',
            width: '12px',
            height: '12px',
            backgroundColor,
          })}
        ></span>
      </x-flex>
    `;
  };

  const plugin = getPluginForScope('registration::product-waitinglist-priority-rules', doc.institutionID);
  return html`
    <x-flex vertical space="xs">
      ${segment(doc.participationCount, doc.capacity)}
      ${(() => {
        if (plugin.id === 'registration::product-waitinglist-priority-rules::default') return nothing;
        return segment(doc.highPriorityParticipationCount, doc.capacity);
      })()}
    </x-flex>
  `;
}

function renderDataViewEntryOptions(doc, design) {
  const institutionID = doc.institutionID;
  return html`
    <x-flex space="xs" horizontal crossaxis-start ${css({ justifyContent: 'end' })}>
      <tr-button-small
        icon="edit"
        .action=${e => {
          goTo(`/admin/institution-details/${institutionID}/workshops/detail/${doc._id}`);
          e.stopPropagation();
        }}
        .color=${design?.colors.secondary.shade(200)}
      ></tr-button-small>

      <tr-button-small
        icon="description"
        .action=${e => {
          goTo(`/admin/institution-details/${institutionID}/workshops/notes/${doc._id}`);
          e.stopPropagation();
        }}
        .color=${design?.colors.secondary.shade(200)}
      ></tr-button-small>

      ${(() => {
        if (!doc.isCanceled)
          return html`
            <tr-button-small
              .color=${design?.colors.tone.error}
              icon="do_not_disturb_on"
              .action=${async (e: MouseEvent) => {
                e.stopPropagation();
                const confirmed = await XDialog.confirm(clientTranslator.translate('workshop', 'confirm_cancelation'));
                if (confirmed) {
                  await XDialog.waitFor(setProductCancleStatus(doc._id, true)());
                }
              }}
              .color=${design?.colors.secondary.shade(200)}
            ></tr-button-small>
          `;
        else
          return html`
            <tr-button-small
              .color=${design?.colors.tone.success}
              icon="do_not_disturb_off"
              .action=${async e => {
                e.stopPropagation();
                const confirmed = await XDialog.confirm(
                  clientTranslator.translate('workshop', 'confirm_decancelation'),
                );
                if (confirmed) {
                  await XDialog.waitFor(setProductCancleStatus(doc._id, false)());
                }
              }}
              .color=${design?.colors.secondary.shade(200)}
            ></tr-button-small>
          `;
      })()}
    </x-flex>
  `;
}

async function exportWorkshops(institutionID: Maybe<string>, timeframeID?: string) {
  if (!institutionID) return;

  try {
    await XDialog.waitFor(
      Promise.all([
        Workshop.exportWorkshopCSV(institutionID, timeframeID)().then(fileName =>
          downloadLink(A.absoluteUrl(`/__export/download/${fileName}`), 'workshops.csv'),
        ),
        firstValueFrom(timer(500)),
      ]),
    );
  } catch (e) {
    await XDialog.alert(clientTranslator.translate('workshop', 'export_err'));
  }
}

async function importWorkshops(institutionID: Maybe<string>) {
  if (!institutionID) throw new Error('instituionID not set');

  const result = await askForTextFile({ acceptFileTypes: 'csv' });
  if (result) {
    Workshop.importWorkshopsForInstitution(result, institutionID)().subscribe({
      next: importMessage => console.log({ importMessage }),
      error: e => {
        console.log(e);
        void XSnackbar.show(clientTranslator.translate('err', 'could_not_process_data'));
      },
      complete: () => {
        void XSnackbar.show(clientTranslator.translate('actions', 'that_worked'));
      },
    });
  }
}
